import React, { useState, useEffect } from "react";
import Navbar from "../Navbar/Navbar";
import SearchTabs from "../Home/SearchTabs/SearchTabs";
import FlightSidebar from "./FlightSidebar/FlightSidebar";
import FlightsContent from "./FlightsContent/FlightsContent";
import Footer from "../Footer/Footer";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { post } from "../../utils/request";
import ErrorModal from "../ui/commons/ErrorModal";
import { AppError } from "../../utils/customError";
import {FloatButton} from "antd";
import "./flights.scss";
import FlightsHorizontalDateStrip from "./FlightsHorizontalDateStrip/FlightsHorizontalDateStrip";
import {get} from "../../utils/request";
import SEO, { SITE_URL } from "../SEO/SEO";


const Flights = () => {
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [flights, setFlights] = useState([]);
  const [flightSearchResult, setFlightSearchResult] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [passenger, setPassenger] = useState({});

  const [flightSearchParams, setFlightSearchParams] = useState({});

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [urlSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [useInlineButton, setUseInlineButton] = useState(false);
  const ENTER_THRESHOLD = 300;
  const LEAVE_THRESHOLD = 300;

  const [filterTags, setFilterTags] = useState([]);

  const [flightsLocationSelections, setFlightsLocationSelections] = useState([{from: null, to: null, departDateTime: null, arriveDateTime: null}]);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

      if (!useInlineButton && distanceToBottom <= ENTER_THRESHOLD) {
        setUseInlineButton(true);
      } else if (useInlineButton && distanceToBottom > LEAVE_THRESHOLD) {
        setUseInlineButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [useInlineButton]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      if (mobile) {
        setIsSidebarHidden(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggleSidebar = () => {
    setIsSidebarHidden((prev) => !prev);
  };

  async function fetchSearchLocations(query) {
    try {
      const result = await get("/flights/searchLocations", { query });
      return result;
    } catch (err) {
      return [];
    }
  }

  async function fetchSearchLocationsByCityCode(cityCode) {
    try {
      const result = await fetchSearchLocations(cityCode);
      
      for (let i = 0; i < result.length; i++) {
        if (result[i].cityCode === cityCode) {
          return result[i];
        }
      }

      return null;
    } catch (err) {
      return null;
    }
  }

  async function initLocationSelections(flightsSearchParams) {
    const { journeyType, journeyRoutes } = flightsSearchParams;

    const requests = journeyRoutes.map(async (route) => {
      const [depart, arrive] = await Promise.all([
        fetchSearchLocationsByCityCode(route.departCityCode),
        fetchSearchLocationsByCityCode(route.arriveCityCode),
      ]);

      return { depart, arrive };
    });

    const results = await Promise.all(requests);

    const locationSels = journeyRoutes.map((route, i) => {
      const { depart, arrive } = results[i];

      if (journeyType === "ow" || journeyType === "rt") {
        return {
          from: depart,
          to: arrive,
          departDateTime: route.departDate,
          arriveDateTime: i === 0 ? journeyRoutes[1]?.departDate : null,
        };
      }

      // multi
      return {
        from: depart,
        to: arrive,
        departDateTime: route.departDate,
      };
    });

    setFlightsLocationSelections(locationSels);
  }

  useEffect(() => {
    let searchParams = parseFlightSearchParams();
    if (!searchParams) {
      setFlightSearchParams({});
      setFlightSearchResult({});
      setFlightsLocationSelections([
        { from: null, to: null, departDateTime: null, arriveDateTime: null },
      ]);
      return;
    }
    setFlightSearchParams(searchParams);

    initLocationSelections(searchParams);

    loadFlights(searchParams);
  }, [urlSearchParams, location.state]);

  async function loadFlights(searchParams) {
    if (searchParams.journeyNo === 1) {
      fetchFlights(searchParams);
    } else {
      fetchFlightsList(searchParams);
    }
  }

  async function fetchFlights(searchParams) {
    setIsLoading(true);
    try {
      let searchResult = await post("/flights/search", searchParams);
      setFlightSearchResult(searchResult);
    } catch (err) {
      if (err instanceof AppError && err.code === 30001) {
        showErrorInfo(err.message);
      }

      setFlightSearchResult({});
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchPatchFlights(searchParams) {
    setIsLoading(true);
    try {
      let searchResult = await post("/flights/patchSearch", searchParams);
      setFlightSearchResult(searchResult.list[0]);
    } catch (err) {
      if (err instanceof AppError && err.code === 30001) {
        showErrorInfo(err.message);
      }

      setFlightSearchResult({});
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchFlightsList(searchParams) {
    setIsLoading(true);
    try {
      let searchResult = await post("/flights/flightListSearch", searchParams);
      setFlightSearchResult(searchResult);
    } catch (err) {
      if (err instanceof AppError && err.code === 30001) {
        showErrorInfo(err.message);
      }

      setFlightSearchResult({});
    } finally {
      setIsLoading(false);
    }
  }

  function showErrorInfo(errorMessage) {
    setErrorMessage(errorMessage);
    setShowError(true);
  }

  function onErrorConfirmed() {
    setShowError(false);
    navigate(buildFlightsUrl(flightSearchParams, 1));
  }

  async function sortBy(sortBy) {
    flightSearchParams.sortBy = sortBy;
    setFlightSearchParams(flightSearchParams);
    await loadFlights(flightSearchParams);
  }

  function toJourneyType(tripType) {
    if (tripType === "round-trip") {
      return "rt";
    } else if (tripType === "one-way") {
      return "ow";
    } else if (tripType === "multi-city") {
      return "multi";
    } else {
      return null;
    }
  }

  function toCabin(cabin) {}

  function parseFlightSearchParams() {
    let journeyType = toJourneyType(urlSearchParams.get("tripType"));
    if (!journeyType) {
      return null;
    }

    let searchParams = {
      journeyType: journeyType,
      passengers: {
        adults: Number(urlSearchParams.get("adults")),
        children: Number(urlSearchParams.get("children")),
        infants: Number(urlSearchParams.get("infants")),
      },
      filterCriteria: {
        cabin: urlSearchParams.get("cabin"),
      },
      sortBy: "cheapest",
    };

    let journeyRoutes = [];
    if (journeyType === "rt") {
      journeyRoutes.push({
        departAirportCode: urlSearchParams.get("depratureAirport"),
        arriveAirportCode: urlSearchParams.get("arrivalAirport"),
        departCityCode: urlSearchParams.get("departureCity"),
        arriveCityCode: urlSearchParams.get("arrivalCity"),
        departDate: urlSearchParams.get("departureDate"),
      });
      journeyRoutes.push({
        departAirportCode: urlSearchParams.get("arrivalAirport"),
        arriveAirportCode: urlSearchParams.get("depratureAirport"),
        departCityCode: urlSearchParams.get("arrivalCity"),
        arriveCityCode: urlSearchParams.get("departureCity"),
        departDate: urlSearchParams.get("returnDate"),
      });
    } else if (journeyType === "ow") {
      journeyRoutes.push({
        departAirportCode: urlSearchParams.get("depratureAirport"),
        arriveAirportCode: urlSearchParams.get("arrivalAirport"),
        departCityCode: urlSearchParams.get("departureCity"),
        arriveCityCode: urlSearchParams.get("arrivalCity"),
        departDate: urlSearchParams.get("departureDate"),
      });
    } else if (journeyType === "multi") {
      for (let i = 1; i <= 6; i++) {
        if (i <= 2) {
          journeyRoutes.push({
            departAirportCode: urlSearchParams.get(`depratureAirport${i}`),
            arriveAirportCode: urlSearchParams.get(`arrivalAirport${i}`),
            departCityCode: urlSearchParams.get(`departureCity${i}`),
            arriveCityCode: urlSearchParams.get(`arrivalCity${i}`),
            departDate: urlSearchParams.get(`departureDate${i}`),
          });
        } else if (
          !!urlSearchParams.get(`departureCity${i}`) &&
          !!urlSearchParams.get(`arrivalCity${i}`) &&
          !!urlSearchParams.get(`departureDate${i}`)
        ) {
          journeyRoutes.push({
            departAirportCode: urlSearchParams.get(`depratureAirport${i}`),
            arriveAirportCode: urlSearchParams.get(`arrivalAirport${i}`),
            departCityCode: urlSearchParams.get(`departureCity${i}`),
            arriveCityCode: urlSearchParams.get(`arrivalCity${i}`),
            departDate: urlSearchParams.get(`departureDate${i}`),
          });
        }
      }
    }

    let journeyNo = Number(urlSearchParams.get("jn")) ?? 1;
    let selectedItineraries = location.state?.selectedItineraries ?? undefined;

    searchParams.journeyRoutes = journeyRoutes;
    searchParams.journeyNo = journeyNo;
    searchParams.selectedItineraries = selectedItineraries;

    return searchParams;
  }

  function buildFlightsSearchParamsWithFilter(filterTags) {
    let searchParams = structuredClone(flightSearchParams);

    //airlines
    searchParams.filterCriteria["airlines"] = filterTags
      .filter((tag) => tag.key.startsWith("airline:"))
      .map((tag) => tag.value);

    //times
    const departFilterTag = (filterTags || []).find(tag => tag.key === 'depart');
    if (!!departFilterTag) {
      searchParams.filterCriteria["departTimeRange"] = {
        start: departFilterTag.value.start,
        end: departFilterTag.value.end
      }
    }
    const arriveFilterTag = (filterTags || []).find(tag => tag.key === 'arrive');
    if (!!arriveFilterTag) {
      searchParams.filterCriteria["arriveTimeRange"] = {
        start: arriveFilterTag.value.start,
        end: arriveFilterTag.value.end
      }
    }

    //stops
    const stopsFilterTag = (filterTags || []).find(tag => tag.key === 'stops');
    if (!!stopsFilterTag) {
      searchParams.filterCriteria["stops"] = stopsFilterTag.value;
    }

    return searchParams;
  }

  async function fetchFlightsWithFilter(filterTags) {
    let searchParams = buildFlightsSearchParamsWithFilter(filterTags);

    await fetchFlights(searchParams);
  }

  async function selectFlightsHorizontalDate(selectedDates) {
    let searchParams;
    if (flightSearchParams.journeyType === "rt") {
      searchParams = buildFlightsSearchParamsWithFilter(filterTags);
      let patches = [];
      for (let i = 0; i < 2; i++) {
        let patch = structuredClone(searchParams);
        patch.departDate = selectedDates[i];

        patches.push(patch);
      }

      searchParams.patches = patches;

      flightSearchParams.journeyRoutes[0].departDate = selectedDates[0];
      flightSearchParams.journeyRoutes[1].departDate = selectedDates[1];
    } else if (flightSearchParams.journeyType === "ow") {
      searchParams = buildFlightsSearchParamsWithFilter(filterTags);
      let patch = structuredClone(searchParams);
      patch.departDate = selectedDates[0];

      searchParams.patches = [patch];

      flightSearchParams.journeyRoutes[0].departDate = selectedDates[0];
    }

    setFlightSearchParams(flightSearchParams);
    await fetchPatchFlights(searchParams);
  }

  return (
    <>
      <SEO
        title="Search Flights & Compare Trip Essentials | Teleport Now"
        description="Search round-trip, one-way, and multi-city flights with Teleport Now, then manage eSIMs, travel deals, bookings, rewards, and support in one travel app."
        canonicalPath="/flights"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "Teleport Now",
              url: SITE_URL,
              logo: `${SITE_URL}/logo512.png`,
            },
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/flights#webpage`,
              name: "Search Flights with Teleport Now",
              description:
                "Search round-trip, one-way, and multi-city flights, then keep travel eSIMs, deals, rewards, bookings, and support together in Teleport Now.",
              url: `${SITE_URL}/flights`,
              isPartOf: {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                name: "Teleport Now",
                url: SITE_URL,
              },
              about: [
                {
                  "@type": "Service",
                  name: "Flight search",
                  provider: {
                    "@id": `${SITE_URL}/#organization`,
                  },
                },
                {
                  "@type": "SoftwareApplication",
                  "@id": `${SITE_URL}/#app`,
                  name: "Teleport Now",
                  applicationCategory: "TravelApplication",
                },
              ],
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: SITE_URL,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Flights",
                  item: `${SITE_URL}/flights`,
                },
              ],
            },
          ],
        }}
      />
      <Navbar />
      <SearchTabs
        flightsLocationSelections={flightsLocationSelections}
        setFlightsLocationSelections={setFlightsLocationSelections}
      />

      {flightSearchParams?.journeyType ? <div className="flights-page-content">
        <div className="container" style={{marginBottom: '20px'}}>
          {!isLoading &&
            flightSearchParams.journeyNo === 1 &&
            !!flightSearchResult &&
            !!flightSearchResult?.itineraryGroups &&
            !!flightSearchResult.itineraryGroups.length > 0 &&
            !!flightSearchResult?.quickCalendar && (
              <div
                className=""
                style={{ marginTop: "5px", marginBottom: "20px" }}
              >
                <FlightsHorizontalDateStrip
                  flightSearchParams={flightSearchParams}
                  quickCalendar={flightSearchResult?.quickCalendar}
                  onSelected={selectFlightsHorizontalDate}
                />
              </div>
            )}

          {isLoading ? (
            <div className="loader"></div>
          ) : (
            <div>
              <div className="row">
                <div
                  className={`col ${isSidebarHidden ? "d-none" : ""}`}
                  style={{ flex: "0 0 30%" }}
                >
                  <FlightSidebar
                    isLoading={isLoading}
                    isHidden={isSidebarHidden}
                    onToggle={handleToggleSidebar}
                    flightSearchParams={flightSearchParams}
                    flightSearchResult={flightSearchResult}
                    fetchFlights={fetchFlights}
                    filterTags={filterTags}
                    setFilterTags={setFilterTags}
                    fetchFlightsWithFilter={fetchFlightsWithFilter}
                  />
                </div>
                {!!flightSearchResult &&
                flightSearchResult.itineraryGroups?.length === 0 ? (
                  <div className="col" style={{ flex: "0 0 70%" }}>
                    <div
                      style={{
                        margin: "350px auto",
                        textAlign: "center",
                        color: "#333",
                      }}
                    >
                      No flights found
                    </div>
                  </div>
                ) : (
                  <div className="col" style={{ flex: "0 0 70%" }}>
                    <FlightsContent
                      isLoading={isLoading}
                      onToggle={handleToggleSidebar}
                      flights={flights}
                      passenger={passenger}
                      flightSearchParams={flightSearchParams}
                      flightSearchResult={flightSearchResult}
                      sortBy={sortBy}
                      flightsLocationSelections={flightsLocationSelections}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isLoading && !useInlineButton && (
          <FloatButton.BackTop
            shape="square"
            type="primary"
            style={{ backgroundColor: "#CBA135", borderColor: "#CBA135" }}
            rootClassName="dynamic-back-top"
          />
        )}
        {!isLoading && useInlineButton && (
          <div
            style={{
              marginTop: -55,
              marginRight: 20,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <FloatButton.BackTop
              type="primary"
              shape="square"
              onClick={scrollToTop}
              style={{
                position: "static",
                backgroundColor: "#CBA135",
                borderColor: "#CBA135",
              }}
            />
          </div>
        )}
      </div> : (
        <section className="flights-search-intro" aria-label="Start a flight search">
          <p className="teleport-eyebrow">Search live flight options</p>
          <h1>Choose your route to compare flights.</h1>
          <p>Set your airports, dates, travellers, and cabin above. Results and live fares will appear here.</p>
        </section>
      )}

      <ErrorModal
        open={showError}
        message={errorMessage}
        buttonText="OK"
        onConfirmed={onErrorConfirmed}
      />

      <Footer />
    </>
  );
};

export default Flights;

export function toTripType(journeyType) {
  if (journeyType === "rt") {
    return "round-trip";
  } else if (journeyType === "ow") {
    return "one-way";
  } else if (journeyType === "multi") {
    return "multi-city";
  } else {
    return null;
  }
}

export function buildFlightsUrl(flightSearchParams, journeyNo) {
  let journeyType = flightSearchParams.journeyType;

  if (journeyType === "rt") {
    let url = `/flights?tripType=${toTripType(
      flightSearchParams.journeyType
    )}&departureCity=${
      flightSearchParams.journeyRoutes[0].departCityCode
    }&arrivalCity=${
      flightSearchParams.journeyRoutes[0].arriveCityCode
    }&departureDate=${
      flightSearchParams.journeyRoutes[0].departDate
    }&returnDate=${
      flightSearchParams.journeyRoutes[1].departDate
    }&adults=${
      flightSearchParams.passengers.adults
    }&children=${
      flightSearchParams.passengers.children
    }&infants=${
      flightSearchParams.passengers.infants
    }&cabin=${flightSearchParams.filterCriteria.cabin

    }&jn=${journeyNo}`;

    if (!!flightSearchParams.journeyRoutes[0].departAirportCode) {
      url += `&depratureAirport=${flightSearchParams.journeyRoutes[0].departAirportCode}`;
    }

    if (!!flightSearchParams.journeyRoutes[0].arriveAirportCode) {
      url += `&arrivalAirport=${flightSearchParams.journeyRoutes[0].arriveAirportCode}`;
    }

    return url;
  } else if (journeyType === "ow") {
    let url = `/flights?tripType=${toTripType(
      flightSearchParams.journeyType
    )}&departureCity=${
      flightSearchParams.journeyRoutes[0].departCityCode
    }&arrivalCity=${
      flightSearchParams.journeyRoutes[0].arriveCityCode
    }&departureDate=${
      flightSearchParams.journeyRoutes[0].departDate
    }&adults=${
      flightSearchParams.passengers.adults
    }&children=${flightSearchParams.passengers.children}&infants=${
      flightSearchParams.passengers.infants
    }&cabin=${flightSearchParams.filterCriteria.cabin}&jn=${journeyNo}`;

    if (!!flightSearchParams.journeyRoutes[0].departAirportCode) {
      url += `&depratureAirport=${flightSearchParams.journeyRoutes[0].departAirportCode}`;
    }

    if (!!flightSearchParams.journeyRoutes[0].arriveAirportCode) {
      url += `&arrivalAirport=${flightSearchParams.journeyRoutes[0].arriveAirportCode}`;
    }

    return url;
  } else if (journeyType === "multi") {
    let url = `/flights?tripType=${toTripType(
      flightSearchParams.journeyType
    )}&adults=${
      flightSearchParams.passengers.adults
    }&children=${flightSearchParams.passengers.children}&infants=${
      flightSearchParams.passengers.infants
    }&cabin=${flightSearchParams.filterCriteria.cabin}&jn=${journeyNo}`;

    for (let i = 0; i < flightSearchParams.journeyRoutes.length; i++) {
      let index = i + 1;
      url += `&departureCity${index}=${flightSearchParams.journeyRoutes[i].departCityCode}`;
      url += `&arrivalCity${index}=${flightSearchParams.journeyRoutes[i].arriveCityCode}`;
      url += `&departureDate${index}=${flightSearchParams.journeyRoutes[i].departDate}`;

      if (!!flightSearchParams.journeyRoutes[i].departAirportCode) {
        url += `&depratureAirport${index}=${flightSearchParams.journeyRoutes[i].departAirportCode}`;
      }

      if (!!flightSearchParams.journeyRoutes[i].arriveAirportCode) {
        url += `&arrivalAirport${index}=${flightSearchParams.journeyRoutes[i].arriveAirportCode}`;
      }
    }

    return url;
  }
}
